import {
  AtticSettings,
  ComponentSide,
  EndConditionSettings,
  isFeaturePatternTruss,
  isProfileTruss,
  MaterialSelection,
  OverhangSettings,
  ProfileLine,
  TraySettings,
} from '@paragon/contract-library';
import { slopeToDegrees } from '@paragon/geometry-library';
import { hasEndConditionEditingFeatureFlag } from '@paragon/login-tools';
import { getHangerListsRecord, getLumberLists, getPlateListsRecord } from '@paragon/material-lists';

import { getAtticFloorWebLength } from '../../components/interface/subpanels/properties/AtticSettings';
import { SaveAction } from '../../components/interface/subpanels/settings/SettingsSaveButton';
import { findProfileFixedOrFeatureJointXLocation } from '../../library/profiles/profileManipulation';
import type { AppDispatch, RootState } from '../../store';
import { rewebTruss, startRewebTruss } from '../../store/actions/editComponentActions';
import { editEndCondition, updateActiveFeaturePatternTrussSettings } from '../../store/actions/editorActions';
import { cancelInsertion } from '../../store/actions/insertActions';
import {
  chooseInsertionComponentType,
  commitInsertComponentDesign,
  startInsertComponentDesign,
  updateShapeDetailsAndRegenerateTruss,
} from '../../store/actions/insertComponentDesignActions';
import { saveMaterialSelection } from '../../store/actions/inventorySetActions';
import { updateActiveAtticSettings, updateNumberOfPanelsOnWebJointSegment } from '../../store/actions/profileActions';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getProjectLayoutModel } from '../../store/selectors/getProjectLayoutModel';
import { getInsertingProfileOrNull } from '../../store/selectors/profileSelectors';
import {
  clearRewebbingInfo,
  ConfigurableShapeDetails,
  setInsertionComponentType,
} from '../../store/slices/applicationSlice';
import { setTrussEditsPanel } from '../../store/slices/panelDataSlice';
import {
  findTrussByName,
  NO_PROFILE_ALTERNATIVE,
  openTruss,
  requireProject,
  requireString,
  summarizeTruss,
  waitForAppToSettle,
  waitForRewebToStart,
} from './helpers';
import { PROFILE_GEOMETRY_SCHEMA, readProfileGeometry } from './profileGeometry';
import { applySettingChanges } from './settingsTools';
import { applyProfileGeometry } from './trussGeometryTools';
import { defineTool, ToolError } from './types';

const TRUSS_SCHEMA = { type: 'string', description: 'The truss’s name.' };

/** The roof shapes the Assistant can start a truss from, as the app’s Create > Component dialog offers them. */
export const TRUSS_SHAPES = ['Common', 'Gable', 'Jack', 'Scissors', 'Valley', 'Attic', 'Piggyback'] as const;
type TrussShape = (typeof TRUSS_SHAPES)[number] | 'Floor';
const CREATABLE_SHAPES: readonly TrussShape[] = [...TRUSS_SHAPES, 'Floor'];

interface CreateTrussInput {
  readonly shape: TrussShape;
  readonly span: number;
  readonly pitch?: number;
  readonly bottomPitch?: number;
  readonly overhang?: number;
  readonly heelHeight?: number;
  readonly name?: string;
  readonly outline?: unknown;
  readonly attic?: AtticRoom;
  readonly depth?: number;
  readonly trays?: readonly { x: number; width: number; stepUp: number; stepDown?: number }[];
}

const TRAYS_SCHEMA = {
  type: 'array',
  description:
    'Floor only: raised parts of the bottom chord, such as a coffered ceiling, as the Interior Conditions panel ' +
    'adds a tray. x is where the bottom chord starts to step up, in inches from the left end; width runs from ' +
    'there to where it steps back down; stepUp and stepDown are how far it rises and drops (stepDown defaults to ' +
    'stepUp).',
  items: {
    type: 'object',
    properties: {
      x: { type: 'number' },
      width: { type: 'number' },
      stepUp: { type: 'number' },
      stepDown: { type: 'number' },
    },
    required: ['x', 'width', 'stepUp'],
  },
};

/** Makes a floor truss as Create > Component > Floor does, with its length, depth, and trays set. */
async function createFloorTruss(dispatch: AppDispatch, getState: () => RootState, input: CreateTrussInput) {
  if (!Number.isFinite(input.span) || input.span <= 0)
    throw new ToolError('A floor truss needs a "span" (its length).');
  if (input.depth != null && (!Number.isFinite(input.depth) || input.depth <= 0))
    throw new ToolError('"depth" must be more than 0 inches.');
  const trays: TraySettings[] = (input.trays ?? []).map((tray, index) => {
    for (const key of ['x', 'width', 'stepUp'] as const)
      if (!Number.isFinite(tray?.[key]) || tray[key] <= 0)
        throw new ToolError(`trays[${index}].${key} must be more than 0 inches.`);
    if (tray.x + tray.width >= input.span) throw new ToolError(`trays[${index}] runs past the truss’s right end.`);
    return {
      type: 'tray',
      guid: crypto.randomUUID(),
      distanceFromLeft: tray.x,
      stepUp: tray.stepUp,
      trayWidth: tray.width,
      stepDown: tray.stepDown ?? tray.stepUp,
    };
  });

  await dispatch(chooseInsertionComponentType('FloorTruss', 'FeaturePattern', 'default', 'FeaturePattern'));
  const settings = getState().application.insertingObjectInfo;
  if (settings?.type !== 'componentDesign' || settings.designSettings.definitionType !== 'FeaturePattern')
    throw new ToolError('Could not start a new floor truss.');
  await dispatch(
    updateActiveFeaturePatternTrussSettings({
      ...settings.designSettings,
      width: input.span,
      depth: input.depth ?? settings.designSettings.depth,
      interiorConditionSettings: [...settings.designSettings.interiorConditionSettings, ...trays],
    }),
  );
  await waitForAppToSettle(getState);
}

interface AtticRoom {
  readonly center?: number;
  readonly width?: number;
  readonly height?: number;
  readonly floorDepth?: number;
  readonly singleFloorChord?: boolean;
}

const ATTIC_ROOM_SCHEMA = {
  type: 'object',
  description:
    'Attic shape only: the room, as the truss editor’s Attic settings set it, in inches. Anything left out keeps ' +
    'the project’s default, except that the room is centered on the span unless `center` says otherwise.',
  properties: {
    center: { type: 'number', description: 'x of the room’s center, from the truss’s left end.' },
    width: { type: 'number', description: 'Between the inside faces of the room’s walls.' },
    height: { type: 'number', description: 'From the top of the floor to the bottom of the ceiling (collar tie).' },
    floorDepth: {
      type: 'number',
      description:
        'The floor’s depth. With singleFloorChord, the depth of the bottom chord that is the floor, such as 11.25 ' +
        'for a 2x12.',
    },
    singleFloorChord: {
      type: 'boolean',
      description:
        'True when the room’s floor is the bottom chord itself (a deeper chord between the walls), as most drawings ' +
        'show; false for a webbed floor raised above the bottom chord.',
    },
  },
};

/**
 * Applies the designer's room to an attic's default settings. The default room isn't centered on the span, so it
 * gets centered there unless a center is given.
 */
export function applyAtticRoom(defaults: AtticSettings, room: AtticRoom, span: number): AtticSettings {
  for (const key of ['center', 'width', 'height', 'floorDepth'] as const) {
    const value = room[key];
    if (value != null && (!Number.isFinite(value) || value <= 0))
      throw new ToolError(`attic.${key} must be more than 0 inches.`);
  }
  const width = room.width ?? defaults.width;
  return {
    ...defaults,
    middle: room.center ?? span / 2,
    width,
    height: room.height ?? defaults.height,
    floorDepth: room.floorDepth ?? defaults.floorDepth,
    useSingleFloorChord: room.singleFloorChord ?? defaults.useSingleFloorChord,
    zoneSettings:
      width === defaults.width
        ? defaults.zoneSettings
        : {
            ...defaults.zoneSettings,
            horizontalWebLength: getAtticFloorWebLength(width, defaults.zoneSettings.maxWebRun),
          },
  };
}

function withOverhang(overhang: OverhangSettings, distance: number | undefined): OverhangSettings {
  return distance == null ? overhang : { ...overhang, distance };
}

/**
 * Applies the designer's numbers to the app's default details for a shape, leaving the rest of the defaults alone.
 */
export function applyShapeDetails(
  defaults: ConfigurableShapeDetails,
  input: CreateTrussInput,
): ConfigurableShapeDetails {
  if (!Number.isFinite(input.span) || input.span <= 0) throw new ToolError('Span must be more than 0 inches.');
  if (input.overhang != null && input.overhang < 0) throw new ToolError('Overhang can’t be negative.');
  const slope = input.pitch != null ? slopeToDegrees(input.pitch) : undefined;
  const heel = <T extends { heelMethod: string }>(settings: T): T =>
    input.heelHeight == null ? settings : { ...settings, heelMethod: 'HeelHeight', heelHeight: input.heelHeight };

  switch (defaults.type) {
    case 'Common':
    case 'Gable':
    case 'Attic':
      return {
        ...defaults,
        span: input.span,
        ...(slope != null ? { slopeInDegrees: slope } : {}),
        heelSettings: heel(defaults.heelSettings),
        overhang: withOverhang(defaults.overhang, input.overhang),
      };
    case 'Jack':
      return {
        ...defaults,
        span: input.span,
        ...(slope != null ? { slopeInDegrees: slope } : {}),
        leftHeelSettings: heel(defaults.leftHeelSettings),
        leftOverhang: withOverhang(defaults.leftOverhang, input.overhang),
      };
    case 'Scissors':
      return {
        ...defaults,
        span: input.span,
        ...(slope != null ? { topChordSlopeInDegrees: slope } : {}),
        ...(input.bottomPitch != null ? { bottomChordSlopeInDegrees: slopeToDegrees(input.bottomPitch) } : {}),
        heelSettings: heel(defaults.heelSettings),
        overhang: withOverhang(defaults.overhang, input.overhang),
      };
    case 'Valley':
    case 'Piggyback':
      return { ...defaults, span: input.span, ...(slope != null ? { slopeInDegrees: slope } : {}) };
    case 'FreeformProfile':
      throw new ToolError('Freeform profiles are drawn on the canvas; choose a shape instead.');
  }
}

/**
 * Lists a profile's web joint segments, the stretches of chord whose panel counts Re-web sets, by an id the
 * Assistant can name them by.
 */
export function describeWebSegments(profile: {
  profileJoints: readonly { guid: string; location: { x: number; y: number } }[];
  profileLines: readonly ProfileLine[];
}) {
  // Lines are numbered within their chord type, so ids match between the saved profile and the one Re-web starts
  // from, which leaves out the heels.
  const linesSeenOfType = new Map<string, number>();
  return profile.profileLines.flatMap((line) => {
    const lineNumber = (linesSeenOfType.get(line.profileLineType) ?? 0) + 1;
    linesSeenOfType.set(line.profileLineType, lineNumber);
    if (line.webJointSegments.length === 0) return [];
    const jointX = (guid: string) => findProfileFixedOrFeatureJointXLocation(profile as never, line, guid);

    return line.webJointSegments.map((segment, segmentIndex) => ({
      id: `${line.profileLineType}-${lineNumber}.${segmentIndex + 1}`,
      guid: segment.guid,
      chord: line.profileLineType,
      fromX: Math.round(Math.min(jointX(segment.baseJointGuid), jointX(segment.endJointGuid)) * 16) / 16,
      toX: Math.round(Math.max(jointX(segment.baseJointGuid), jointX(segment.endJointGuid)) * 16) / 16,
      panels: segment.numberOfFloatingJoints == null ? 'auto' : segment.numberOfFloatingJoints + 1,
    }));
  });
}

type ListKind = 'lumber' | 'plate' | 'hanger';

function getListsByKind(state: RootState): Record<ListKind, { guid: string; name: string }[]> {
  const toList = (record: unknown) =>
    Object.entries((record ?? {}) as Record<string, { guid?: string; name: string }>).map(([guid, list]) => ({
      guid: list.guid ?? guid,
      name: list.name,
    }));
  return {
    lumber: toList(getLumberLists(state)),
    plate: toList(getPlateListsRecord(state)),
    hanger: toList(getHangerListsRecord(state)),
  };
}

function findList(state: RootState, kind: ListKind, name: string) {
  const lists = getListsByKind(state)[kind];
  const list = lists.find((candidate) => candidate.name.trim().toLowerCase() === name.trim().toLowerCase());
  if (list == null)
    throw new ToolError(
      `There is no ${kind} list named "${name}". The ${kind} lists are ${lists.map((l) => l.name).join(', ')}.`,
    );
  return list;
}

function describeSelection(state: RootState, selection: MaterialSelection | undefined) {
  if (selection == null) return null;
  const lists = getListsByKind(state);
  const nameOf = (kind: ListKind, guid: string) => lists[kind].find((list) => list.guid === guid)?.name ?? guid;
  return {
    lumberList: nameOf('lumber', selection.lumberListGuid),
    plateList: nameOf('plate', selection.plateListGuid),
    hangerList: nameOf('hanger', selection.hangerListGuid),
  };
}

interface MaterialListInput {
  readonly lumberList?: string;
  readonly plateList?: string;
  readonly hangerList?: string;
  readonly trusses?: string[];
  readonly applyToExistingTrusses?: boolean;
}

export const trussSetupTools = [
  defineTool<CreateTrussInput>({
    name: 'create_truss',
    description:
      'Creates a new truss from a shape, without a layout, as Create > Component does: Common, Gable, Jack, ' +
      'Scissors, Valley, Attic, or Piggyback, or Floor for a floor truss (span is its length; give `depth` and any ' +
      '`trays`). Span, overhang, and heel height are in inches; pitch is rise per 12 (bottomPitch for a scissors’ ' +
      'bottom chord). Other details come from the project’s defaults; an attic’s room comes from `attic`. To match a ' +
      'drawing exactly (monopitch, different heels, cantilevers, bearings, panel points), also give `outline` and ' +
      'the other geometry, as set_truss_profile takes it; the shape then only supplies cut and bearing defaults ' +
      '(use Common unless the truss is a jack) and span may be left out. Returns the new truss, open in the truss ' +
      'editor; run Upgrade and Analyze afterward, which also gives every member its own name (a new attic numbers ' +
      'each part of it from W1).',
    inputSchema: {
      type: 'object',
      properties: {
        shape: { type: 'string', enum: CREATABLE_SHAPES },
        span: { type: 'number' },
        pitch: { type: 'number' },
        bottomPitch: { type: 'number' },
        overhang: { type: 'number' },
        heelHeight: { type: 'number' },
        name: { type: 'string' },
        attic: ATTIC_ROOM_SCHEMA,
        depth: { type: 'number', description: 'Floor only: the truss’s depth in inches.' },
        trays: TRAYS_SCHEMA,
        ...PROFILE_GEOMETRY_SCHEMA,
      },
      required: ['shape'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      if (!CREATABLE_SHAPES.includes(input.shape))
        throw new ToolError(`"shape" must be one of ${CREATABLE_SHAPES.join(', ')}.`);
      if (input.shape !== 'Floor' && (input.depth != null || input.trays != null))
        throw new ToolError('"depth" and "trays" are for the Floor shape only.');
      if (input.shape === 'Floor' && input.outline != null)
        throw new ToolError('A floor truss is made from its length, depth, and trays, not an outline.');
      const geometry = input.outline == null ? null : readProfileGeometry(input as unknown as Record<string, unknown>);
      if (geometry == null && typeof input.span !== 'number') throw new ToolError('Give "span", or an "outline".');
      const span = geometry == null ? input.span : Math.max(...geometry.outline.map((point) => point.x));
      const before = getActiveComponentDesign(getState())?.guid ?? null;

      await dispatch(startInsertComponentDesign());
      // A step that fails leaves the new truss half-made in the insertion panel, so close it.
      try {
        if (input.shape === 'Floor') await createFloorTruss(dispatch, getState, input);
        else await createRoofTruss(input.shape);
        if (input.name != null && input.name.trim() !== '')
          dispatch(
            setInsertionComponentType({
              componentType: input.shape === 'Floor' ? 'FloorTruss' : 'RoofTruss',
              name: input.name.trim(),
            }),
          );
        await dispatch(commitInsertComponentDesign());
        await waitForAppToSettle(getState);
      } catch (error) {
        if (getState().application.insertingObjectInfo != null) dispatch(cancelInsertion());
        throw error;
      }

      const created = getActiveComponentDesign(getState());
      if (created == null || created.guid === before || getState().application.insertingObjectInfo != null)
        throw new ToolError('The app could not create the truss. Check its notifications for why.');
      return { summary: summarizeTruss(created) };

      async function createRoofTruss(shape: (typeof TRUSS_SHAPES)[number]) {
        await dispatch(chooseInsertionComponentType('RoofTruss', 'Profile', 'default', shape));
        const insertion = getState().application.insertingObjectInfo;
        if (insertion?.type !== 'componentDesign' || insertion.shapeDetails == null)
          throw new ToolError('Could not start a new truss.');

        await dispatch(
          updateShapeDetailsAndRegenerateTruss(applyShapeDetails(insertion.shapeDetails, { ...input, span })),
        );
        await waitForAppToSettle(getState);
        if (input.shape === 'Attic') {
          const atticSettings = getInsertingProfileOrNull(getState())?.atticSettings;
          if (atticSettings == null) throw new ToolError('The new attic truss has no room to set.');
          await dispatch(updateActiveAtticSettings(applyAtticRoom(atticSettings, input.attic ?? {}, span)));
          await waitForAppToSettle(getState);
        } else if (input.attic != null) throw new ToolError('"attic" is for the Attic shape only.');
        if (geometry != null) await applyProfileGeometry(dispatch, getState, geometry);
      }
    },
  }),

  defineTool<{ truss: string; side: ComponentSide; changes: Record<string, unknown> }>({
    name: 'edit_end_condition',
    description:
      'Edits one end of a floor truss, as its End Condition panel does: cutbacks, top-chord bearing, ribbons, ' +
      'bearing blocks, end verticals, and so on. Read the current settings with get_end_conditions and give only ' +
      'what changes. Floor (feature pattern) trusses only.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        side: { type: 'string', enum: ['Left', 'Right'] },
        changes: {
          type: 'object',
          description: 'For example {"topChordCutBackDistance": 12, "isTopChordBearing": true}.',
        },
      },
      required: ['truss', 'side', 'changes'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      if (!hasEndConditionEditingFeatureFlag(getState()))
        throw new ToolError('This account can’t edit end conditions yet; it needs the edit-end-conditions feature.');
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      const component = componentDesign.component;
      if (!isFeaturePatternTruss(component))
        throw new ToolError('Only floor (feature pattern) trusses have end conditions.');
      const inferred = component.inferredSettings;
      if (inferred == null) throw new ToolError('The app couldn’t read this truss’s settings to edit its ends.');
      if (input.side !== 'Left' && input.side !== 'Right') throw new ToolError('"side" must be Left or Right.');

      const current: EndConditionSettings =
        input.side === 'Left' ? inferred.leftEndSettings : inferred.rightEndSettings;
      const { settings, changed } = applySettingChanges(current, input.changes);
      if (changed.length === 0) return { summary: { changed: [] } };

      const succeeded = await dispatch(editEndCondition(componentDesign, settings));
      if (!succeeded) throw new ToolError('The app could not apply that end condition.');
      await waitForAppToSettle(getState);
      return { summary: { changed, truss: summarizeTruss(getState().componentDesigns.data[componentDesign.guid]) } };
    },
  }),

  defineTool<{ truss: string }>({
    name: 'get_end_conditions',
    description: 'Reads both end conditions of a floor truss, with every setting edit_end_condition can change.',
    inputSchema: { type: 'object', properties: { truss: TRUSS_SCHEMA }, required: ['truss'] },
    changesProject: false,
    async execute(input, { getState }) {
      const component = findTrussByName(getState(), requireString(input, 'truss')).component;
      if (!isFeaturePatternTruss(component) || component.inferredSettings == null)
        throw new ToolError('Only floor (feature pattern) trusses have end conditions to read.');
      return {
        summary: {
          left: component.inferredSettings.leftEndSettings,
          right: component.inferredSettings.rightEndSettings,
        },
      };
    },
  }),

  defineTool<{ truss: string }>({
    name: 'get_web_segments',
    description:
      'Reads a profile truss’s web segments, each stretch of chord between fixed joints, with its id, chord, x extent ' +
      '(inches), and panel count, so you can plan a re-web with reweb_truss.',
    inputSchema: { type: 'object', properties: { truss: TRUSS_SCHEMA }, required: ['truss'] },
    changesProject: false,
    async execute(input, { getState }) {
      const componentDesign = findTrussByName(getState(), requireString(input, 'truss'));
      if (!isProfileTruss(componentDesign.component) || componentDesign.profile == null)
        throw new ToolError(
          'Only profile trusses have web segments. get_truss gives every member’s centerline and outline instead.',
        );
      return {
        summary: {
          truss: componentDesign.component.name,
          webSegments: describeWebSegments(componentDesign.profile).map(({ guid: _guid, ...segment }) => segment),
        },
      };
    },
  }),

  defineTool<{ truss: string; panels?: { segment: string; count: number | 'auto' }[] }>({
    name: 'reweb_truss',
    description:
      'Re-webs a profile truss, as Edit Truss > Reweb does. Give panel counts for segments by the ids ' +
      'get_web_segments returns ("auto" lets the app choose), or none to re-web as is. Run Upgrade and Analyze ' +
      'afterward.',
    inputSchema: {
      type: 'object',
      properties: {
        truss: TRUSS_SCHEMA,
        panels: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              segment: { type: 'string', description: 'A segment id, such as TopChord-1.1.' },
              count: { description: 'A number of panels, or "auto".' },
            },
            required: ['segment', 'count'],
          },
        },
      },
      required: ['truss'],
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const componentDesign = await openTruss(dispatch, getState, requireString(input, 'truss'));
      if (!isProfileTruss(componentDesign.component))
        throw new ToolError(`Only profile trusses can be re-webbed. ${NO_PROFILE_ALTERNATIVE}`);

      await dispatch(startRewebTruss());
      await waitForRewebToStart(getState);
      try {
        const profile = () => {
          const rewebbingProfile = getState().application.rewebbingInfo?.profileSettings.profile;
          if (rewebbingProfile == null) throw new ToolError('Could not start re-webbing this truss.');
          return rewebbingProfile;
        };

        for (const { segment, count } of input.panels ?? []) {
          const found = describeWebSegments(profile()).find((candidate) => candidate.id === segment);
          if (found == null)
            throw new ToolError(
              `There is no web segment "${segment}". The segments are ` +
                `${describeWebSegments(profile())
                  .map((candidate) => candidate.id)
                  .join(', ')}.`,
            );
          if (count !== 'auto' && (!Number.isInteger(count) || count < 1))
            throw new ToolError('A panel count must be 1 or more, or "auto".');

          const webJointSegment = profile()
            .profileLines.flatMap((line) => line.webJointSegments)
            .find((candidate) => candidate.guid === found.guid)!;
          await dispatch(updateNumberOfPanelsOnWebJointSegment(webJointSegment, count === 'auto' ? null : count));
          await waitForAppToSettle(getState);
        }
        if ((input.panels ?? []).length === 0) {
          await dispatch(rewebTruss());
          await waitForAppToSettle(getState);
        }

        const segments = describeWebSegments(profile()).map(({ guid: _guid, ...segment }) => segment);
        return {
          summary: {
            webSegments: segments,
            truss: summarizeTruss(getState().componentDesigns.data[componentDesign.guid]),
          },
        };
      } finally {
        dispatch(clearRewebbingInfo());
        dispatch(setTrussEditsPanel(null));
      }
    },
  }),

  defineTool<{ truss?: string }>({
    name: 'get_material_lists',
    description:
      'Reads the lumber, plate, and hanger lists the organization has, and which ones the project or a truss uses.',
    inputSchema: { type: 'object', properties: { truss: TRUSS_SCHEMA } },
    changesProject: false,
    async execute(input, { getState }) {
      const state = getState();
      requireProject(state);
      const lists = getListsByKind(state);
      return {
        summary: {
          available: {
            lumberLists: lists.lumber.map(({ name }) => name),
            plateLists: lists.plate.map(({ name }) => name),
            hangerLists: lists.hanger.map(({ name }) => name),
          },
          project: describeSelection(state, getProjectLayoutModel(state)?.materialSelection),
          ...(input.truss != null
            ? { truss: describeSelection(state, findTrussByName(state, input.truss).materialSelection) }
            : {}),
        },
      };
    },
  }),

  defineTool<MaterialListInput>({
    name: 'set_material_lists',
    description:
      'Chooses the lumber, plate, or hanger list trusses design from, as Material Settings does. Give trusses to set ' +
      'theirs; otherwise it sets the project’s default, and applyToExistingTrusses also gives it to every truss. ' +
      'Run Upgrade and Analyze afterward.',
    inputSchema: {
      type: 'object',
      properties: {
        lumberList: { type: 'string', description: 'A lumber list name from get_material_lists.' },
        plateList: { type: 'string' },
        hangerList: { type: 'string' },
        trusses: { type: 'array', items: { type: 'string' } },
        applyToExistingTrusses: { type: 'boolean', description: 'Defaults to false.' },
      },
    },
    changesProject: true,
    async execute(input, { dispatch, getState }) {
      const state = getState();
      const projectGuid = requireProject(state);
      if (input.lumberList == null && input.plateList == null && input.hangerList == null)
        throw new ToolError('Give a lumber, plate, or hanger list.');

      const patch = {
        ...(input.lumberList != null ? { lumberListGuid: findList(state, 'lumber', input.lumberList).guid } : {}),
        ...(input.plateList != null ? { plateListGuid: findList(state, 'plate', input.plateList).guid } : {}),
        ...(input.hangerList != null ? { hangerListGuid: findList(state, 'hanger', input.hangerList).guid } : {}),
      };

      if (input.trusses != null && input.trusses.length > 0) {
        for (const name of input.trusses) {
          const componentDesign = findTrussByName(getState(), name);
          await dispatch(
            saveMaterialSelection(
              componentDesign.guid,
              { ...componentDesign.materialSelection, ...patch },
              SaveAction.ActualForSingleComponent,
            ),
          );
        }
        await waitForAppToSettle(getState);
        return {
          summary: {
            trusses: input.trusses.map((name) => ({
              truss: name,
              lists: describeSelection(getState(), findTrussByName(getState(), name).materialSelection),
            })),
          },
        };
      }

      const current = getProjectLayoutModel(state)?.materialSelection;
      if (current == null) throw new ToolError('Could not read the project’s material lists.');
      await dispatch(
        saveMaterialSelection(
          projectGuid,
          { ...current, ...patch },
          input.applyToExistingTrusses ? SaveAction.ActualForHierarchy : SaveAction.DefaultsForHierarchy,
        ),
      );
      await waitForAppToSettle(getState);
      return {
        summary: {
          project: describeSelection(getState(), getProjectLayoutModel(getState())?.materialSelection),
          appliedToExistingTrusses: input.applyToExistingTrusses ?? false,
        },
      };
    },
  }),
];
